import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useRoom } from "./RoomProvider";
import { ACCEPTANCE_CHAIN, acceptanceState, statusLabel } from "../../hooks/useLiveTrips";
import { TpOfferDrawer, TpOfferChip } from "./TpOfferDrawer";
import { apiClient } from "../../lib/api";
import { autoPrefixUS } from "../../components/PhoneInput";
import { offerRideId, offerAgeSeconds } from "../../hooks/useKaizenLiveOffers";
import { ZoneBadge, SameZoneToggle } from "../../components/ZoneBadge";
import { useJsApiLoader } from "@react-google-maps/api";
import TripMap from "../../components/TripMap";

/**
 * FocusWorkspace — the right-side transformation zone.
 *
 * NOT a sidebar. NOT a modal. It doesn't exist until something is focused.
 * When it exists, the room stays alive around it — same substrate, one
 * region transforms into whatever is being worked on.
 *
 * States: trip · driver · provider · hospital · compose · call · broadcast.
 */
/**
 * FocusWorkspace — the cinematic focus room.
 *
 * NOT a sidebar. NOT a modal. Doesn't exist until something is focused.
 * When it exists, it's a full-screen takeover — the ambient map is hidden
 * entirely and the VA is "walked into" a dedicated room built around
 * whatever needs their attention: a trip, a driver, a provider, a call.
 *
 * States: trip · driver · provider · hospital · compose · call · broadcast.
 */
export default function FocusWorkspace() {
  const { focus, releaseFocus, network, identity,
          sosTripId, acknowledgeSos,
          spotlightEvent, dismissSpotlight, undoAutoAlert } = useRoom();
  if (!focus) return null;

  const ROOM_TITLE = {
    realTrip: "trip room", liveDriver: "driver room",
    provider: "provider room", offer: "incoming offer",
    compose: "new trip", composeCpd: "new CPD trip", broadcast: "broadcast room",
  };

  // Live Autonomy Mode — the spotlight only lights up the room it actually
  // matches (the exact trip Nova just acted on), never a stray room the VA
  // happens to be in for an unrelated reason.
  const spotlightActive = spotlightEvent && focus.kind === "realTrip" && focus.id === spotlightEvent.trip_id;

  return (
    <AnimatePresence>
      <motion.div
        className={`cinematic-room ${spotlightActive ? "cinematic-room--nova-glow" : ""}`}
        data-testid="cinematic-room"
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.97 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
      >
        <div className="cinematic-room-grid" />
        <div className="cinematic-room-header">
          <span className="cr-title">{ROOM_TITLE[focus.kind] || "focus"}</span>
          <button className="cr-close" onClick={releaseFocus} title="back to the floor · esc" data-testid="cinematic-room-close">
            × back to the floor
          </button>
        </div>
        {spotlightActive && (
          <NovaSpotlightBanner
            event={spotlightEvent}
            onDismiss={dismissSpotlight}
            onUndo={undoAutoAlert}
          />
        )}
        <div className="cinematic-room-body">
          {focus.kind === "realTrip" && <RealTripView id={focus.id} identity={identity}
                                                      sosTripId={sosTripId} acknowledgeSos={acknowledgeSos} />}
          {focus.kind === "liveDriver" && <LiveDriverView id={focus.id} />}
          {focus.kind === "provider" && <ProviderView id={focus.id} />}
          {focus.kind === "offer" && <OfferFocusView id={focus.id} />}
          {focus.kind === "compose"  && <ComposeTrip  network={network} meta={focus.meta} />}
          {focus.kind === "composeCpd" && <ComposeCpdTrip />}
          {focus.kind === "broadcast" && <BroadcastView network={network} />}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

// ─── Live Autonomy Mode — the spotlight banner ──────────────────────
// Plain-language narration of the exact real event the VA is being shown,
// plus (only when relevant) the one action available in-place — same
// authority/endpoints as doing it from its normal spot on the Floor.
function NovaSpotlightBanner({ event, onDismiss, onUndo }) {
  const [busy, setBusy] = useState(false);
  if (!event) return null;

  const act = event.action;
  const doUndo = async () => {
    setBusy(true);
    try { await onUndo(act.alert_id); toast.success("Undone — back to the previous driver"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't undo that"); }
    finally { setBusy(false); }
  };

  return (
    <div className="nova-spotlight-banner" data-testid="nova-spotlight-banner">
      <span className="nsb-glow" />
      <span className="nsb-text">nova: {event.label}</span>
      <div className="nsb-actions">
        {act?.type === "undo_risk_reassign" && (
          <button className="nsb-btn" disabled={busy} onClick={doUndo} data-testid="nova-spotlight-undo-btn">
            {busy ? "…" : "undo"}
          </button>
        )}
        {act?.type === "manual_assign" && (
          <span className="nsb-hint">assign a driver below — Nova flagged this for you</span>
        )}
        <button className="nsb-dismiss" onClick={onDismiss} data-testid="nova-spotlight-dismiss-btn" title="close this banner">×</button>
      </div>
    </div>
  );
}

// ─── Inline trip editor (VA can patch key fields without leaving the floor) ──
function TripInlineEditor({ trip }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pickupAddr, setPickupAddr] = useState(trip.pickup?.address || "");
  const [dropoffAddr, setDropoffAddr] = useState(trip.dropoff?.address || "");
  const [notes, setNotes] = useState(trip.notes || "");
  const [apptTime, setApptTime] = useState(
    trip.schedule?.appointment_time ? new Date(trip.schedule.appointment_time).toTimeString().slice(0, 5) : ""
  );
  const [pickupTime, setPickupTime] = useState(
    trip.schedule?.requested_pickup ? new Date(trip.schedule.requested_pickup).toTimeString().slice(0, 5) : ""
  );
  const [pickupDate, setPickupDate] = useState(
    trip.schedule?.requested_pickup ? new Date(trip.schedule.requested_pickup).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10)
  );

  if (!open) {
    return (
      <div style={{ padding: "4px 0 8px" }}>
        <button className="ws-verb" onClick={() => setOpen(true)} data-testid={`trip-edit-open-${trip.trip_id}`}
                style={{ width: "100%", justifyContent: "center", opacity: 0.7 }}>
          ✏ edit trip details
        </button>
      </div>
    );
  }

  const save = async () => {
    setSaving(true);
    try {
      const dateBase = pickupDate || new Date().toISOString().slice(0, 10);
      const patch = {};
      if (pickupAddr.trim() !== (trip.pickup?.address || "")) patch.pickup_address = pickupAddr.trim();
      if (dropoffAddr.trim() !== (trip.dropoff?.address || "")) patch.dropoff_address = dropoffAddr.trim();
      if (notes !== (trip.notes || "")) patch.notes = notes;
      if (pickupTime) patch.requested_pickup = new Date(`${dateBase}T${pickupTime}:00`).toISOString();
      if (apptTime) patch.appointment_time = new Date(`${dateBase}T${apptTime}:00`).toISOString();
      if (Object.keys(patch).length === 0) { setOpen(false); return; }
      await apiClient.patch(`/tpt/trips/${trip.trip_id}`, patch);
      toast.success("Trip updated");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update trip");
    } finally { setSaving(false); }
  };

  return (
    <Section kind="ai">
      <Kicker>edit trip details</Kicker>
      <input className="ws-input" placeholder="pickup address"
        value={pickupAddr} onChange={e => setPickupAddr(e.target.value)}
        data-testid={`trip-edit-pickup-${trip.trip_id}`} />
      <input className="ws-input" placeholder="dropoff address" style={{ marginTop: 6 }}
        value={dropoffAddr} onChange={e => setDropoffAddr(e.target.value)}
        data-testid={`trip-edit-dropoff-${trip.trip_id}`} />
      <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
        <div style={{ flex: 1 }}>
          <div className="ws-field-label">Date</div>
          <input className="ws-input" type="date" value={pickupDate}
            onChange={e => setPickupDate(e.target.value)} data-testid={`trip-edit-date-${trip.trip_id}`} />
        </div>
        <div style={{ flex: 1 }}>
          <div className="ws-field-label">Pickup time</div>
          <input className="ws-input" type="time" value={pickupTime}
            onChange={e => setPickupTime(e.target.value)} data-testid={`trip-edit-pickup-time-${trip.trip_id}`} />
        </div>
        <div style={{ flex: 1 }}>
          <div className="ws-field-label">Appt time</div>
          <input className="ws-input" type="time" value={apptTime}
            onChange={e => setApptTime(e.target.value)} data-testid={`trip-edit-appt-time-${trip.trip_id}`} />
        </div>
      </div>
      <div style={{ marginTop: 6 }}>
        <div className="ws-field-label">Driver notes</div>
        <textarea className="ws-input" style={{ minHeight: 60, resize: "vertical" }}
          placeholder="notes for the driver…"
          value={notes} onChange={e => setNotes(e.target.value)}
          data-testid={`trip-edit-notes-${trip.trip_id}`} />
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <button className="ws-primary" style={{ flex: 1 }} onClick={save}
                disabled={saving} data-testid={`trip-edit-save-${trip.trip_id}`}>
          {saving ? "saving…" : "Save changes"}
        </button>
        <button className="ws-verb" onClick={() => setOpen(false)} data-testid={`trip-edit-cancel-${trip.trip_id}`}>
          Cancel
        </button>
      </div>
    </Section>
  );
}

// ─── REAL trip focus card (production data · shared hook) ──────────
// This is what appears when the VA clicks a real trip halo on the map.
// Shows the 7-node acceptance chain, current stage, an "Offer to TPs"
// gate (the marketplace path Live Ops exposes), and reuses the same
// canonical assignment endpoint TPTripsPanel uses (`POST /tpt/trips/{id}/assign`).
function RealTripView({ id, identity, sosTripId, acknowledgeSos }) {
  const { findTrip, assignTrip, broadcastTrip, offerToTps, pinnedTrips, pinTrip, unpinTrip, callSession,
          archiveTrip, hardDeleteTrip, releaseFocus } = useRoom();
  const t = findTrip(id);
  const [assigning, setAssigning] = useState(false);
  const [callingKind, setCallingKind] = useState(null);
  const [offerOpen, setOfferOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const coworker = identity?.coworker_name || "Nova";
  if (!t) return <Empty>trip released</Empty>;

  const doArchive = async () => {
    setArchiving(true);
    try {
      await archiveTrip(t.trip_id);
      toast.success("Trip archived — cleared from the Floor and any open alerts");
      releaseFocus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't archive this trip");
    } finally { setArchiving(false); }
  };

  const doDelete = async () => {
    if (!window.confirm(`Permanently delete this trip (${t.trip_id.slice(0, 8)})? This can't be undone.`)) return;
    setDeleting(true);
    try {
      await hardDeleteTrip(t.trip_id);
      toast.success("Trip permanently deleted");
      releaseFocus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete this trip");
    } finally { setDeleting(false); }
  };

  const chain = acceptanceState(t);
  const isPinned = pinnedTrips.includes(t.trip_id);
  const isSos = sosTripId === t.trip_id;
  const s = String(t.status || "").toUpperCase();
  const canOffer = ["REQUESTED", "PENDING"].includes(s);
  const canAssign = ["REQUESTED", "PENDING", "TP_ACCEPTED"].includes(s);
  const canReassign = ["ASSIGNED", "OFFERED", "EXPIRED_OFFER"].includes(s);
  const pickup = t.pickup?.address || "pickup pending";
  const dropoff = t.dropoff?.address || "dropoff pending";
  const facility = t.dropoff?.facility_name;
  const rider = t.rider?.name || "unknown rider";
  const mob = t.rider?.mobility_type || "amb";
  const tpName = t.assigned_tp?.tp_name;
  const driverName = t.assigned_driver?.driver_name || t.assigned_driver?.name;
  const distance = t.schedule?.estimated_distance_mi;
  const requestedPU = t.schedule?.requested_pickup;
  const timeline = Array.isArray(t.timeline) ? t.timeline : [];
  const offerPool = Array.isArray(t.offer_pool) ? t.offer_pool : [];

  // TTL — when a driver has been offered, they have 90s to accept.
  // The offer_expires_at field is server-authored; if missing, derive from status timestamps.
  const offerExpiresAt = t.offer_expires_at
    || (s === "ASSIGNED" || s === "OFFERED"
        ? deriveOfferExpiry(t)
        : null);

  const doAssign = async (mode) => {
    setAssigning(true);
    try {
      const res = mode === "broadcast"
        ? await broadcastTrip(t.trip_id, { scope: "network", include_independent: true })
        : await assignTrip(t.trip_id, mode === "auto" ? { mode: "auto_assigner" } : {});
      if (!res?.ok) toast.error(res?.error || "Couldn't assign this trip");
    } finally { setAssigning(false); }
  };

  const doCall = async (kind) => {
    if (!callSession) return;
    setCallingKind(kind);
    try {
      const target = kind === "rider"
        ? { kind: "rider", id: t.rider?.id, phone: t.rider?.phone, name: rider }
        : kind === "driver"
          ? { kind: "driver", id: t.assigned_driver?.driver_id, phone: t.assigned_driver?.phone, name: driverName }
          : { kind: "tp", id: t.assigned_tp?.tp_id, name: tpName };
      const res = await callSession.start({
        targets: [target],
        trip_id: t.trip_id,
        purpose: `call_${kind}`,
      });
      if (!res?.ok) toast.error(res?.error || `Couldn't call ${kind}`);
    } finally { setCallingKind(null); }
  };

  return (
    <>
      {isSos && (
        <Section kind="sos">
          <Kicker>🚨 SOS · {String(t.trip_id).slice(0, 8)}</Kicker>
          <H1>{rider}</H1>
          <div className="ws-route">
            <span>{pickup}</span><span className="ws-arrow">→</span><span>{facility || dropoff}</span>
          </div>
          <div className="ws-sos-note">{coworker.toLowerCase()}: sos triggered · the room is on this now.</div>
          <div className="ws-verbs" style={{ marginTop: 14 }}>
            {t.rider?.phone && (
              <button className="ws-verb danger" onClick={() => doCall("rider")} disabled={callingKind === "rider"}
                      data-testid={`sos-call-rider-${t.trip_id}`}>☎ Call rider now</button>
            )}
            {t.assigned_driver && (
              <button className="ws-verb danger" onClick={() => doCall("driver")} disabled={callingKind === "driver"}
                      data-testid={`sos-call-driver-${t.trip_id}`}>☎ Call driver</button>
            )}
          </div>
          <button className="ws-primary" style={{ marginTop: 18, width: "100%", background: "#ef4444" }}
                  onClick={acknowledgeSos} data-testid={`sos-acknowledge-${t.trip_id}`}>
            Acknowledged · release the room
          </button>
        </Section>
      )}

      {!isSos && (
      <>
      <Section kind="mission">
        <Kicker>
          live trip · {String(t.trip_id).slice(0, 8)}
          <button
            className="ws-trip-delete"
            title="permanently delete this trip"
            disabled={deleting}
            onClick={doDelete}
            data-testid={`trip-room-delete-${t.trip_id}`}
          >{deleting ? "deleting…" : "🗑 delete"}</button>
          <button
            className="ws-trip-archive"
            title="archive this trip and clear any open alerts"
            disabled={archiving}
            onClick={doArchive}
            data-testid={`trip-room-archive-${t.trip_id}`}
          >{archiving ? "archiving…" : "📦 archive"}</button>
          <button
            className="ws-pin"
            title={isPinned ? "unpin (P)" : "pin to tray (P)"}
            onClick={() => (isPinned ? unpinTrip(t.trip_id) : pinTrip(t.trip_id))}
          >{isPinned ? "📌 pinned" : "📍 pin"}</button>
        </Kicker>
        <H1>{rider}</H1>
        <div className="ws-route">
          <span>{pickup}</span><span className="ws-arrow">→</span><span>{facility || dropoff}</span>
        </div>
        <StatChips items={[
          { k: "status", v: statusLabel(t.status), tone: chainToneForStatus(t.status) },
          { k: "mobility", v: mob },
          ...(distance ? [{ k: "distance", v: `${Number(distance).toFixed(1)}mi` }] : []),
          ...(tpName ? [{ k: "tp", v: tpName }] : []),
          ...(driverName ? [{ k: "driver", v: driverName }] : []),
          ...(requestedPU ? [{ k: "pickup", v: formatWhen(requestedPU) }] : []),
        ]} />
        {/* Linked legs badge */}
        {t.parent_trip_id && (
          <div style={{ marginTop: 8, fontSize: 11, opacity: 0.65, fontWeight: 600 }}>
            Leg {t.leg_label || "B"} · linked to {String(t.parent_trip_id).slice(0, 8)}
          </div>
        )}
      </Section>

      {/* ───── Inline trip editor ───── */}
      <TripInlineEditor trip={t} />

      {/* ───── Trip Notes — VA editable (broker notes + VA thread) ───── */}
      <TripNotesPanel trip={t} />

      {/* ───── Call ribbon — one-tap breakout room ───── */}
      <Section kind="preflight">
        <Kicker>calls · breakout room · never leave the floor</Kicker>
        <div className="ws-call-ribbon">
          {t.assigned_driver && (
            <button className="ws-call-btn" disabled={callingKind === "driver"}
                    onClick={() => doCall("driver")}
                    data-testid={`call-driver-${t.trip_id}`}>
              📞 driver{callingKind === "driver" ? "…" : ""}
            </button>
          )}
          {t.rider?.phone && (
            <button className="ws-call-btn" disabled={callingKind === "rider"}
                    onClick={() => doCall("rider")}
                    data-testid={`call-rider-${t.trip_id}`}>
              📞 rider{callingKind === "rider" ? "…" : ""}
            </button>
          )}
          {t.assigned_tp && (
            <button className="ws-call-btn" disabled={callingKind === "tp"}
                    onClick={() => doCall("tp")}
                    data-testid={`call-tp-${t.trip_id}`}>
              📞 TP{callingKind === "tp" ? "…" : ""}
            </button>
          )}
        </div>
        {offerExpiresAt && (
          <TTLBadge label="driver acceptance TTL" expiresAt={offerExpiresAt} />
        )}
      </Section>

      {/* ───── The acceptance chain — 7 real gates ───── */}
      <Section kind="preflight">
        <Kicker>acceptance chain</Kicker>
        <ol className="ws-chain" data-testid={`chain-${t.trip_id}`}>
          {ACCEPTANCE_CHAIN.map((node, i) => {
            const done = chain.completed.includes(node.key);
            const active = chain.active === node.key;
            const wasSkipped = chain.skipped.includes(node.key);
            const cls = active ? "active" : done ? "done" : wasSkipped ? "skipped" : "pending";
            return (
              <li key={node.key} className={`ws-chain-node ws-chain-node--${cls}`} data-testid={`chain-node-${node.key}`}
                  title={wasSkipped ? "this trip was assigned directly — the TP marketplace step was never used" : undefined}>
                <span className="ws-chain-idx">{done ? "✓" : active ? "●" : wasSkipped ? "–" : i + 1}</span>
                <span className="ws-chain-label">{node.label}</span>
              </li>
            );
          })}
        </ol>
        {chain.active === "tp_offer" && (
          <div className="ws-chain-note">next: offer to TP(s) for a first-accept marketplace, or assign a driver directly below</div>
        )}
        {chain.active === "tp_accept" && (
          <div className="ws-chain-note">waiting on TP(s) to accept the offer · {coworker} will surface whoever accepts first</div>
        )}
        {chain.active === "assigned" && offerPool.length === 0 && (
          <div className="ws-chain-note">next: pick a TP/driver or broadcast · {coworker} has a shortlist ready</div>
        )}
        {chain.active === "assigned" && offerPool.length > 0 && (
          <div className="ws-chain-note">TP accepted · next: pick a driver from their roster</div>
        )}
        {chain.active === "driver" && (
          <div className="ws-chain-note">driver has ~90s to accept · if they decline, {coworker} auto-preps the next</div>
        )}
        {chain.active === "locked" && (
          <div className="ws-chain-note">locked · both TP and VA see the cycle now to completion</div>
        )}
      </Section>

      {/* ───── Per-TP countdown chips while a multi-TP offer is in flight ───── */}
      {offerPool.length > 0 && s === "OFFERED" && (
        <Section kind="preflight">
          <Kicker>offer pool · first-accept wins</Kicker>
          <div className="ws-offer-pool" data-testid={`offer-pool-${t.trip_id}`}>
            {offerPool.map((o) => (
              <TpOfferChip key={o.tp_id} entry={o} />
            ))}
          </div>
        </Section>
      )}

      {/* ───── One consolidated "get a driver on this" action — was 3 competing
             blocks (fan-out to TPs / pick-a-driver / toggle between them).
             Nova's auto-assign is the single primary move; broadcast-now and
             choosing specific providers are one-line fallbacks, not their own
             panels. Drag-and-drop lives on the ambient map instead. ───── */}
      {(canOffer || canAssign || canReassign) && (
        <Section kind="ai">
          <Kicker>{coworker.toLowerCase()} · {canReassign ? "reassign a driver" : "assign a driver"}</Kicker>
          <button
            className="ws-verb ws-verb--primary"
            onClick={() => doAssign("auto")}
            disabled={assigning}
            data-testid={`assign-auto-${t.trip_id}`}
          >
            ★ {assigning ? "assigning…" : "auto-assign · nova picks the nearest cleared driver"}
          </button>
          <div className="ws-rec-hint">
            {assigning ? "hang tight…" : (
              <>
                nova broadcasts to the network automatically if no match turns up, or{" "}
                <button className="ws-link" onClick={() => doAssign("broadcast")} data-testid={`assign-broadcast-${t.trip_id}`}>broadcast right now</button>
                {canOffer && (
                  <>{" · "}<button className="ws-link" onClick={() => setOfferOpen((v) => !v)} data-testid={`offer-advanced-toggle-${t.trip_id}`}>
                    {offerOpen ? "hide provider list" : "choose specific providers"}
                  </button></>
                )}
              </>
            )}
          </div>
          {offerOpen && canOffer && (
            <div style={{ marginTop: 10 }}>
              <TpOfferDrawer trip={t} onClose={() => setOfferOpen(false)} onOffered={() => setOfferOpen(false)} />
            </div>
          )}
        </Section>
      )}

      {/* ───── Trip timeline (server-authored) ───── */}
      {timeline.length > 0 && (
        <Section kind="evidence">
          <Kicker>timeline · chain of custody</Kicker>
          <ol className="ws-ledger">
            {timeline.slice(-6).map((ev, i) => (
              <li key={i}>
                <span>{ev.event || ev.type || "event"}</span>
                <span>{ev.at ? formatWhen(ev.at) : ""}</span>
              </li>
            ))}
          </ol>
        </Section>
      )}

      {/* ───── Share tracking link — forward to a broker, auto-expires (PHI) ── */}
      <ShareLinkPanel tripId={t.trip_id} />

      {/* ───── Live tracking — the EXACT page the rider/client sees ───── */}
      <LiveTrackingPanel token={t.rider_track_token} />

      {/* ───── Unified comms thread — SMS to rider/driver, replies live here ── */}
      <TripThreadPanel tripId={t.trip_id} hasRiderPhone={!!t.rider?.phone} hasDriver={!!t.assigned_driver} />
      </>
      )}
    </>
  );
}

// ─── Share tracking link — dispatcher-facing forward with auto-expiry (PHI
// compliance). Reuses the same read-only /track/{token} URL the rider's own
// "share my ride" button creates (rider.py), just admin-triggered with a
// configurable TTL and an optional immediate SMS/email forward.
function ShareLinkPanel({ tripId }) {
  const [open, setOpen] = useState(false);
  const [ttl, setTtl] = useState(24);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await apiClient.post(`/floor/trips/${tripId}/share-link`, {
        ttl_hours: ttl,
        forward_phone: phone.trim() || null,
        forward_email: email.trim() || null,
      });
      setResult(data);
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || "couldn't generate link" });
    } finally {
      setBusy(false);
    }
  };

  const copy = () => { if (result?.share_url) navigator.clipboard?.writeText(result.share_url); };

  return (
    <Section kind="preflight">
      <Kicker>
        share tracking link
        <button className="ws-pin" onClick={() => setOpen(v => !v)} data-testid={`share-link-toggle-${tripId}`}>
          {open ? "hide" : "open"}
        </button>
      </Kicker>
      {open && (
        <>
          <div className="ws-rec-hint">read-only, no PHI beyond first names — auto-expires, forward to a broker if needed</div>
          <div className="ws-verbs" style={{ marginTop: 8 }}>
            {[6, 24, 72].map(h => (
              <button key={h} className={`ws-verb ${ttl === h ? "on" : ""}`} onClick={() => setTtl(h)}
                      data-testid={`share-link-ttl-${h}`}>{h}h</button>
            ))}
          </div>
          <input className="ws-input" style={{ marginTop: 8 }} placeholder="broker phone (optional SMS forward)"
                 value={phone} onChange={e => setPhone(e.target.value)}
                 onBlur={() => setPhone((p) => autoPrefixUS(p))}
                 data-testid={`share-link-phone-${tripId}`} />
          <input className="ws-input" style={{ marginTop: 8 }} placeholder="broker email (optional email forward)"
                 value={email} onChange={e => setEmail(e.target.value)} data-testid={`share-link-email-${tripId}`} />
          <button className="ws-primary" style={{ marginTop: 10, width: "100%" }} disabled={busy}
                  onClick={generate} data-testid={`share-link-generate-${tripId}`}>
            {busy ? "generating…" : "Generate & forward"}
          </button>
          {result?.error && <div className="ws-rec-hint" style={{ color: "#ef4444" }}>{result.error}</div>}
          {result?.share_url && (
            <div className="ws-rec-hint" style={{ marginTop: 8 }} data-testid={`share-link-result-${tripId}`}>
              <div style={{ wordBreak: "break-all" }}>{result.share_url}</div>
              <button className="ws-verb" style={{ marginTop: 6 }} onClick={copy}
                      data-testid={`share-link-copy-${tripId}`}>⧉ copy</button>
              <div style={{ marginTop: 4 }}>expires {formatWhen(result.expires_at)}</div>
              {result.sent?.sms && <div>SMS to {result.sent.sms.to}: {result.sent.sms.ok ? "sent ✓" : "failed"}</div>}
              {result.sent?.email && <div>Email to {result.sent.email.to}: {result.sent.email.ok ? "sent ✓" : "failed"}</div>}
            </div>
          )}
        </>
      )}
    </Section>
  );
}

// ─── Unified per-trip comms thread — dispatcher SMS to rider/driver, live
// replies bridged in via the SignalWire inbound webhook (tp_trips.py). Both
// The Floor and Live Ops (TPTripsPanel) read/write the same trip_threads doc.
function TripThreadPanel({ tripId, hasRiderPhone, hasDriver }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [party, setParty] = useState(hasDriver ? "driver" : "rider");
  const [channel, setChannel] = useState("sms");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancel = false;
    const pull = async () => {
      try {
        const { data } = await apiClient.get(`/floor/trips/${tripId}/thread`);
        if (!cancel) setMessages(Array.isArray(data?.messages) ? data.messages : []);
      } catch { /* silent */ }
    };
    pull();
    const t = setInterval(pull, 6000);
    return () => { cancel = true; clearInterval(t); };
  }, [open, tripId]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    setError(null);
    try {
      await apiClient.post(`/floor/trips/${tripId}/thread/send`, { party, channel, body: text.trim() });
      setText("");
      const { data } = await apiClient.get(`/floor/trips/${tripId}/thread`);
      setMessages(Array.isArray(data?.messages) ? data.messages : []);
    } catch (e) {
      setError(e?.response?.data?.detail || `${channel === "email" ? "Email" : "SMS"} failed`);
    } finally {
      setSending(false);
    }
  };

  return (
    <Section kind="preflight">
      <Kicker>
        message thread
        <button className="ws-pin" onClick={() => setOpen(v => !v)} data-testid={`thread-toggle-${tripId}`}>
          {open ? "hide" : "open"}
        </button>
      </Kicker>
      {open && (
        <>
          <ol className="ws-ledger" data-testid={`thread-messages-${tripId}`}>
            {messages.length === 0 && <li><span className="ws-rec-hint">no messages yet</span></li>}
            {messages.map(m => (
              <li key={m.id}>
                <span>{m.direction === "out" ? "→" : "←"} {m.party} · {m.channel === "email" ? "✉" : "sms"} · {m.body}</span>
                <span>{formatWhen(m.at)}</span>
              </li>
            ))}
          </ol>
          <div className="ws-verbs" style={{ marginTop: 8 }}>
            <button className={`ws-verb ${party === "rider" ? "on" : ""}`} disabled={!hasRiderPhone}
                    onClick={() => setParty("rider")} data-testid={`thread-party-rider-${tripId}`}>to rider</button>
            <button className={`ws-verb ${party === "driver" ? "on" : ""}`} disabled={!hasDriver}
                    onClick={() => setParty("driver")} data-testid={`thread-party-driver-${tripId}`}>to driver</button>
            <button className={`ws-verb ${channel === "sms" ? "on" : ""}`}
                    onClick={() => setChannel("sms")} data-testid={`thread-channel-sms-${tripId}`}>sms</button>
            <button className={`ws-verb ${channel === "email" ? "on" : ""}`}
                    onClick={() => setChannel("email")} data-testid={`thread-channel-email-${tripId}`}>email</button>
          </div>
          <textarea className="ws-input" style={{ minHeight: 60, marginTop: 8 }} value={text}
                    onChange={e => setText(e.target.value)} placeholder="type a message…"
                    data-testid={`thread-input-${tripId}`} />
          {error && <div className="ws-rec-hint" style={{ color: "#ef4444" }}>{error}</div>}
          <button className="ws-primary" style={{ marginTop: 8, width: "100%" }} disabled={sending || !text.trim()}
                  onClick={send} data-testid={`thread-send-${tripId}`}>
            {sending ? "sending…" : `Send ${channel === "email" ? "email" : "SMS"}`}
          </button>
        </>
      )}
    </Section>
  );
}


// ─── Live tracking — embeds the ACTUAL public tracking page (same link a
// rider/officer gets) as a read-only iframe, so the VA sees exactly what the
// client sees. No separate map/tracking UI to build or keep in sync.
function LiveTrackingPanel({ token, kind = "rider", tripId }) {
  const [open, setOpen] = useState(false);
  if (!token) return null;
  const path = kind === "cpd" ? `/cpd-track/${token}` : `/r/${token}`;
  const testKey = tripId || token;
  return (
    <Section kind="preflight">
      <Kicker>
        live tracking · what the client sees
        <button className="ws-pin" onClick={() => setOpen(v => !v)} data-testid={`live-tracking-toggle-${testKey}`}>
          {open ? "hide" : "open"}
        </button>
      </Kicker>
      {open && (
        <div className="ws-track-frame" data-testid={`live-tracking-frame-${testKey}`}>
          <iframe src={path} title="live tracking" loading="lazy" />
        </div>
      )}
    </Section>
  );
}

function chainToneForStatus(status) {
  const s = String(status || "").toUpperCase();
  if (["ACCEPTED", "EN_ROUTE", "ARRIVED_PU", "ON_TRIP", "ARRIVED_DO"].includes(s)) return "hi";
  if (["ASSIGNED", "OFFERED", "TP_ACCEPTED"].includes(s)) return "md";
  if (["EXPIRED_OFFER", "OFFER_EXHAUSTED", "NO_SHOW_CONFIRMED"].includes(s)) return "lo";
  if (["COMPLETED"].includes(s)) return "hi";
  return null;
}

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    return sameDay ? `today ${time}` : `${d.toLocaleDateString([], { month: "short", day: "numeric" })} ${time}`;
  } catch { return String(iso); }
}

/**
 * TTLBadge — live-updating countdown badge.
 *
 * Applied to any deadline surface: driver-accept window, offer expiry, SOS
 * escalation timer. Turns amber under 30s, red under 10s, dead when 0.
 */
function TTLBadge({ label, expiresAt }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const target = new Date(expiresAt).getTime();
  const remaining = Math.max(0, Math.round((target - Date.now()) / 1000));
  const cls = remaining === 0 ? "ttl--dead"
    : remaining < 10 ? "ttl--red"
    : remaining < 30 ? "ttl--amber"
    : "ttl--ok";
  const mmss = `${String(Math.floor(remaining / 60)).padStart(2, "0")}:${String(remaining % 60).padStart(2, "0")}`;
  // eslint-disable-next-line no-unused-vars
  const _tick = tick;
  return (
    <div className={`ttl ${cls}`} data-testid="ttl-badge">
      <span className="ttl-label">{label}</span>
      <span className="ttl-count">{remaining === 0 ? "EXPIRED" : mmss}</span>
    </div>
  );
}

/**
 * deriveOfferExpiry — best-effort inference when the server hasn't stored
 * `offer_expires_at` yet. Driver-acceptance windows are 90s from the last
 * OFFERED transition in the timeline.
 */
function deriveOfferExpiry(trip) {
  const timeline = Array.isArray(trip.timeline) ? trip.timeline : [];
  const offered = [...timeline].reverse().find(e =>
    ["OFFERED_MULTI_TP", "OFFERED", "ASSIGNED"].includes((e.event || "").toUpperCase())
  );
  if (!offered?.at) return null;
  try {
    return new Date(new Date(offered.at).getTime() + 90 * 1000).toISOString();
  } catch { return null; }
}

// ─── LIVE driver view — real GPS driver, clicked from the map puck or the
// command line. Every action here hits a real endpoint; there is no mock
// fallback. This is the "co-pilot" card the VA walkthrough was missing.
function LiveDriverView({ id }) {
  const { liveDrivers, realProviders, callSession, smsDriver, setDriverShift, focusOn } = useRoom();
  const d = (liveDrivers || []).find(x => x.driver_id === id);
  const [smsOpen, setSmsOpen] = useState(false);
  const [smsText, setSmsText] = useState("");
  const [smsSending, setSmsSending] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [earningsOpen, setEarningsOpen] = useState(false);
  const [earnings, setEarnings] = useState(null);
  const [offerOpen, setOfferOpen] = useState(false);
  const [openTrips, setOpenTrips] = useState(null);
  const [busy, setBusy] = useState(false);

  if (!d) return <Empty>driver off the board · GPS went stale</Empty>;
  const online = String(d.shift_status || "").toLowerCase() !== "offline";
  const hasPhone = !!d.phone_masked;
  const tpName = d.tp_id ? (realProviders || []).find(p => p.tp_id === d.tp_id)?.name || "linked provider" : "independent";

  const doCall = async () => {
    setBusy(true);
    try {
      // Don't pass d.phone — /tpt/map/candidates masks it for display
      // ("***-***-5555"). Passing just the id lets floor_calls resolve the
      // real number server-side from db.drivers.
      const res = await callSession.start({
        targets: [{ kind: "driver", id: d.driver_id, name: d.name }],
        purpose: "call_driver",
      });
      if (!res?.ok) toast.error(res?.error || "Couldn't start the call");
    } finally { setBusy(false); }
  };

  const toggleShift = async () => {
    setBusy(true);
    try { await setDriverShift(d.driver_id, online ? "offline" : "available", d.name); }
    finally { setBusy(false); }
  };

  const sendSms = async () => {
    if (!smsText.trim()) return;
    setSmsSending(true);
    const res = await smsDriver(d.driver_id, smsText.trim(), d.name);
    setSmsSending(false);
    if (res.ok) { setSmsText(""); setSmsOpen(false); }
  };

  const loadHistory = async () => {
    if (historyOpen) { setHistoryOpen(false); return; }
    setHistoryOpen(true);
    if (history) return;
    try {
      const { data } = await apiClient.get(`/floor/drivers/${d.driver_id}/trips`);
      setHistory(data.items || []);
    } catch { setHistory([]); }
  };

  const loadEarnings = async () => {
    if (earningsOpen) { setEarningsOpen(false); return; }
    setEarningsOpen(true);
    if (earnings) return;
    try {
      const { data } = await apiClient.get(`/floor/drivers/${d.driver_id}/earnings`);
      setEarnings(data);
    } catch { setEarnings({ error: true }); }
  };

  const loadOpenTrips = async () => {
    if (offerOpen) { setOfferOpen(false); return; }
    setOfferOpen(true);
    if (openTrips) return;
    try {
      const { data } = await apiClient.get("/tpt/trips", { params: { status: "REQUESTED" } });
      setOpenTrips(Array.isArray(data) ? data : (data?.items || []));
    } catch { setOpenTrips([]); }
  };

  const offerTripTo = async (trip) => {
    setBusy(true);
    try {
      await apiClient.post(`/tpt/trips/${trip.trip_id}/assign`, {
        tp_id: d.tp_id, driver_id: d.driver_id,
      });
      setOfferOpen(false);
      focusOn("realTrip", trip.trip_id);
    } catch { /* surfaced via whisper on the assign path elsewhere */ }
    finally { setBusy(false); }
  };

  return (
    <>
      <Section kind="mission">
        <Kicker>driver · live</Kicker>
        <H1>{d.name}</H1>
        <StatChips items={[
          { k: "shift", v: d.shift_status || "unknown", tone: online ? "hi" : "lo" },
          { k: "provider", v: d.tp_name || tpName },
          { k: "phone", v: d.phone_masked || "none on file" },
        ]} />
      </Section>

      {d.current_trip_id && (
        <Section kind="ai">
          <Kicker>on a trip right now</Kicker>
          <button className="ws-verb" data-testid={`live-driver-goto-trip-${d.driver_id}`}
                  onClick={() => focusOn("realTrip", d.current_trip_id)}>
            → open trip {String(d.current_trip_id).slice(0, 8)}
          </button>
        </Section>
      )}

      <Section kind="actions">
        <Kicker>i can</Kicker>
        <div className="ws-verbs">
          <button className="ws-verb" disabled={!hasPhone || busy} onClick={doCall}
                  data-testid={`live-driver-call-${d.driver_id}`}>☎ Call</button>
          <button className="ws-verb" onClick={() => setSmsOpen(v => !v)}
                  data-testid={`live-driver-sms-toggle-${d.driver_id}`}>✎ SMS</button>
          <button className="ws-verb" onClick={loadOpenTrips}
                  data-testid={`live-driver-offer-toggle-${d.driver_id}`}>◐ Offer trip</button>
          <button className="ws-verb" disabled={busy} onClick={toggleShift}
                  data-testid={`live-driver-shift-toggle-${d.driver_id}`}>
            {online ? "◍ Go offline" : "◍ Go online"}
          </button>
          <button className="ws-verb" onClick={loadHistory}
                  data-testid={`live-driver-history-toggle-${d.driver_id}`}>≡ Trip history</button>
          <button className="ws-verb" onClick={loadEarnings}
                  data-testid={`live-driver-earnings-toggle-${d.driver_id}`}>$ Earnings</button>
        </div>
      </Section>

      {smsOpen && (
        <Section kind="preflight">
          <Kicker>text {d.name}</Kicker>
          <textarea className="ws-input" style={{ minHeight: 70 }} value={smsText}
                    onChange={e => setSmsText(e.target.value)} placeholder="what should they know?"
                    data-testid={`live-driver-sms-text-${d.driver_id}`} />
          <button className="ws-primary" style={{ marginTop: 8 }} disabled={smsSending || !smsText.trim()}
                  onClick={sendSms} data-testid={`live-driver-sms-send-${d.driver_id}`}>
            {smsSending ? "sending…" : "Send"}
          </button>
        </Section>
      )}

      {offerOpen && (
        <Section kind="preflight">
          <Kicker>open trips · tap to offer to {d.name}</Kicker>
          {openTrips === null && <div className="ws-rec-hint">loading…</div>}
          {openTrips?.length === 0 && <div className="ws-rec-hint">no unassigned trips right now</div>}
          {openTrips?.map(t => (
            <button key={t.trip_id} className="ws-verb" style={{ width: "100%", marginBottom: 6 }}
                    disabled={busy} onClick={() => offerTripTo(t)}
                    data-testid={`live-driver-offer-trip-${t.trip_id}`}>
              {t.rider?.name || "rider"} · {t.pickup?.address?.split(",")[0] || "pickup"}
            </button>
          ))}
        </Section>
      )}

      {historyOpen && (
        <Section kind="evidence">
          <Kicker>trip history</Kicker>
          {history === null && <div className="ws-rec-hint">loading…</div>}
          {history?.length === 0 && <div className="ws-rec-hint">no completed trips yet</div>}
          <ol className="ws-ledger">
            {(history || []).map(t => (
              <li key={t.trip_id}>
                <span>{t.rider?.name || "rider"} · {t.status}</span>
                <span>{t.updated_at ? formatWhen(t.updated_at) : ""}</span>
              </li>
            ))}
          </ol>
        </Section>
      )}

      {earningsOpen && (
        <Section kind="evidence">
          <Kicker>this month</Kicker>
          {earnings === null && <div className="ws-rec-hint">loading…</div>}
          {earnings?.error && <div className="ws-rec-hint">couldn't load earnings</div>}
          {earnings && !earnings.error && (
            <StatChips items={[
              { k: "rides", v: earnings.rides },
              { k: "payouts", v: `$${Number(earnings.driver_payouts || 0).toFixed(0)}` },
              { k: "tier", v: earnings.tier?.label || "—" },
              { k: "net est.", v: `$${Number(earnings.net_payout_estimate || 0).toFixed(0)}` },
            ]} />
          )}
        </Section>
      )}
    </>
  );
}

// ─── Provider view ─────────────────────────────────────────────────
// Reached two ways: the full-page Providers roster (/floor/providers) and
// the acceptance-chain focus real trips resolve through, always using the
// real TP's `tp_id`.
function OfferFocusView({ id }) {
  const { incomingOffers, offerBusy, offerSuggestions, loadOfferSuggestions,
          acceptOffer, declineOffer, releaseFocus } = useRoom();
  const [confirming, setConfirming] = useState(null); // "accept" | "decline" | null
  const [sameZoneOnly, setSameZoneOnly] = useState(false);
  const [coords, setCoords] = useState({ pickup: null, dropoff: null });
  const { isLoaded: mapsLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries: ["places"],
  });

  const offer = (incomingOffers || []).find(o => offerRideId(o) === id);
  const off = offer?.offer || {};

  // Offers only carry plain address strings pre-acceptance (no lat/lng yet,
  // that only gets geocoded once a trip is actually created) — geocode
  // on the fly so the VA can see the route at a glance without leaving
  // this room. One request per address change, not on every render.
  useEffect(() => {
    if (!mapsLoaded || !window.google?.maps) return;
    if (!off.pickup_address && !off.dropoff_address) return;
    const geocoder = new window.google.maps.Geocoder();
    const geocodeOne = (address) => new Promise((resolve) => {
      if (!address) { resolve(null); return; }
      geocoder.geocode({ address }, (results, status) => {
        if (status === "OK" && results?.[0]?.geometry?.location) {
          const loc = results[0].geometry.location;
          resolve({ lat: loc.lat(), lng: loc.lng() });
        } else resolve(null);
      });
    });
    let cancelled = false;
    Promise.all([geocodeOne(off.pickup_address), geocodeOne(off.dropoff_address)]).then(([pu, dof]) => {
      if (!cancelled) setCoords({ pickup: pu, dropoff: dof });
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapsLoaded, off.pickup_address, off.dropoff_address]);

  useEffect(() => {
    if (!id) return;
    if (offerSuggestions[id] === undefined) loadOfferSuggestions(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!offer) return <Empty>offer no longer on the board · already resolved or expired</Empty>;

  const age = offerAgeSeconds(offer);
  const ranked = offerSuggestions[id];
  const busy = offerBusy[id];
  const isBusy = !!busy;

  const runAccept = async () => {
    setConfirming(null);
    await acceptOffer(id);
  };
  const runDecline = async () => {
    setConfirming(null);
    await declineOffer(id);
  };

  return (
    <>
      <Section kind="mission">
        <Kicker>kaizen · incoming offer</Kicker>
        <H1>{off.patient_name || "unnamed rider"}</H1>
        <StatChips items={[
          { k: "mobility", v: (off.mobility_type || "amb").toString().toUpperCase() },
          { k: "when", v: `${off.pickup_date || ""} ${off.pickup_time || ""}`.trim() || "—" },
          { k: "distance", v: off.distance_miles || off.miles ? `${Number(off.distance_miles || off.miles).toFixed(1)} mi` : "—" },
          { k: "age", v: age != null ? (age < 60 ? `${age}s ago` : `${Math.round(age / 60)}m ago`) : "—", tone: age > 30 ? "lo" : "hi" },
        ]} />
      </Section>

      <Section kind="info">
        <Kicker>route</Kicker>
        <div className="il-card-route">
          <div className="il-route-line">
            <span className="il-pt il-pt--pu">PU</span>
            <span className="il-addr">{off.pickup_address || "pickup pending"}</span>
          </div>
          <div className="il-route-line">
            <span className="il-pt il-pt--do">DO</span>
            <span className="il-addr">{off.dropoff_address || "dropoff pending"}</span>
          </div>
        </div>
        {(coords.pickup || coords.dropoff) && (
          <div style={{ marginTop: 10 }} data-testid={`offer-focus-map-${id}`}>
            <TripMap
              pickup={coords.pickup ? { ...coords.pickup, label: off.pickup_address } : null}
              dropoff={coords.dropoff ? { ...coords.dropoff, label: off.dropoff_address } : null}
              theme="dark"
              height={180}
            />
          </div>
        )}
      </Section>

      <Section kind="ai">
        <div className="il-nova-head">
          <span className="il-nova-dot" />
          <span className="il-nova-label">nova · pre-verified shortlist</span>
          {Array.isArray(ranked) && ranked.length > 0 && (
            <SameZoneToggle checked={sameZoneOnly} onChange={setSameZoneOnly} testId={`offer-focus-same-zone-${id}`} style={{ marginLeft: "auto" }} />
          )}
        </div>
        {ranked === undefined && <div className="il-nova-loading">ranking providers…</div>}
        {Array.isArray(ranked) && ranked.length === 0 && (
          <div className="il-nova-loading il-nova-err">no matches in service area · manual review</div>
        )}
        {Array.isArray(ranked) && ranked.length > 0 && (
          <ul className="il-rank">
            {(sameZoneOnly ? ranked.filter((r) => r.same_zone) : ranked).slice(0, 5).map((r, i) => (
              <li key={`${r.kind}-${r.id}-${i}`} className={`il-rank-row il-rank-row--${r.match_band || "unk"}`}
                  data-testid={`offer-focus-rank-${id}-${i}`}>
                <span className="il-rank-idx">{i + 1}</span>
                <span className="il-rank-name">{r.name || `${r.kind} ${String(r.id).slice(0, 6)}`}</span>
                <span className="il-rank-meta">
                  {r.distance_mi != null ? `${Number(r.distance_mi).toFixed(1)}mi` : "—"}
                  <ZoneBadge zoneName={r.zone_name} sameZone={r.same_zone} />
                  {r.match_band === "green" && <span className="il-band il-band--g">in-area</span>}
                  {r.match_band === "yellow" && <span className="il-band il-band--y">edge</span>}
                  {r.match_band === "red" && <span className="il-band il-band--r">out</span>}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section kind="actions">
        {confirming ? (
          <div className="il-confirm-actions" data-testid={`offer-focus-confirm-${id}`}>
            <span className="ws-kicker">
              {confirming === "accept" ? "accept on kaizen?" : "decline on kaizen?"}
            </span>
            <button className="il-btn il-btn--decline" onClick={() => setConfirming(null)}
                    data-testid={`offer-focus-confirm-cancel-${id}`}>cancel</button>
            <button className={`il-btn ${confirming === "accept" ? "il-btn--accept" : "il-btn--danger"}`}
                    onClick={confirming === "accept" ? runAccept : runDecline}
                    data-testid={`offer-focus-confirm-proceed-${id}`}>
              {confirming === "accept" ? "yes, accept" : "yes, decline"}
            </button>
          </div>
        ) : (
          <div className="il-actions">
            <button className="il-btn il-btn--accept" disabled={isBusy}
                    onClick={() => setConfirming("accept")} data-testid={`offer-focus-accept-${id}`}>
              {busy === "accept" ? "accepting on kaizen…" : "accept"}
            </button>
            <button className="il-btn il-btn--decline" disabled={isBusy}
                    onClick={() => setConfirming("decline")} data-testid={`offer-focus-decline-${id}`}>
              {busy === "decline" ? "declining…" : "decline"}
            </button>
            <button className="ws-verb" onClick={releaseFocus} data-testid={`offer-focus-skip-${id}`}>
              → back to the floor
            </button>
          </div>
        )}
      </Section>
    </>
  );
}

function ProviderView({ id }) {
  const { realProviders, liveDrivers, callSession, smsProvider, cameraTo } = useRoom();
  const [rosterOpen, setRosterOpen] = useState(false);
  const [roster, setRoster] = useState(null);
  const [billingOpen, setBillingOpen] = useState(false);
  const [billing, setBilling] = useState(null);
  const [contractOpen, setContractOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgText, setMsgText] = useState("");
  const [msgSending, setMsgSending] = useState(false);
  const [coverageNote, setCoverageNote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [portalLaunching, setPortalLaunching] = useState(false);
  const [portalLinkData, setPortalLinkData] = useState(null);
  const [issuingLink, setIssuingLink] = useState(false);

  const real = (realProviders || []).find(x => x.tp_id === id);
  if (!real) return <Empty>provider released</Empty>;

  const tpDrivers = (liveDrivers || []).filter(d => d.tp_id === real.tp_id);
  const name = real.name;

  const loadRoster = async () => {
    if (rosterOpen) { setRosterOpen(false); return; }
    setRosterOpen(true);
    if (roster) return;
    try {
      const { data } = await apiClient.get(`/tpt/providers/${real.tp_id}/drivers`);
      setRoster(Array.isArray(data) ? data : []);
    } catch { setRoster([]); }
  };

  const loadBilling = async () => {
    if (billingOpen) { setBillingOpen(false); return; }
    setBillingOpen(true);
    if (!real) return;
    if (billing) return;
    try {
      const { data } = await apiClient.get(`/tpt/providers/${real.tp_id}/billing`);
      setBilling(data);
    } catch { setBilling({ error: true }); }
  };

  const doCall = async () => {
    if (!real?.contact_phone) return;
    setBusy(true);
    try {
      const res = await callSession.start({ targets: [{ kind: "tp", id: real.tp_id, name: real.name }], purpose: "call_tp" });
      if (!res?.ok) toast.error(res?.error || "Couldn't start the call");
    } finally { setBusy(false); }
  };

  const sendMsg = async () => {
    if (!msgText.trim() || !real) return;
    setMsgSending(true);
    const res = await smsProvider(real.tp_id, msgText.trim(), real.name);
    setMsgSending(false);
    if (res.ok) { setMsgText(""); setMsgOpen(false); }
  };

  const openCoverage = () => {
    const withGps = tpDrivers.filter(d => d.last_seen_lat != null);
    if (withGps.length === 0) {
      setCoverageNote("no live GPS from this provider's drivers right now");
      return;
    }
    setCoverageNote(null);
    const lat = withGps.reduce((s, d) => s + d.last_seen_lat, 0) / withGps.length;
    const lng = withGps.reduce((s, d) => s + d.last_seen_lng, 0) / withGps.length;
    cameraTo({ center: [lat, lng], zoom: 11 });
  };

  const openTPPortal = async () => {
    setPortalLaunching(true);
    try {
      const { data } = await apiClient.post(`/tpt/providers/${real.tp_id}/impersonate`);
      if (data?.portal_url) {
        window.open(data.portal_url, "_blank", "noopener,noreferrer");
        toast.success(`Opened ${name}'s portal · link valid for 30 min`);
      } else {
        toast.error("No portal URL returned");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open TP portal");
    } finally { setPortalLaunching(false); }
  };

  const issuePortalLink = async () => {
    setIssuingLink(true);
    try {
      const { data } = await apiClient.post(`/tpt/${real.tp_id}/issue-portal`);
      setPortalLinkData(data);
      toast.success(`Portal access issued — link valid 48 hrs · email sent to ${data.dispatcher_email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't issue portal link");
    } finally { setIssuingLink(false); }
  };

  return (
    <>
      <Section kind="mission">
        <Kicker>provider</Kicker>
        <H1>{name}</H1>
        <StatChips items={[
          { k: "drivers", v: tpDrivers.length },
          { k: "online", v: tpDrivers.filter(d => String(d.shift_status).toLowerCase() !== "offline").length, tone: "hi" },
          { k: "status", v: real.status || "active", tone: real.status === "active" ? "hi" : "md" },
          { k: "auto-assign", v: real.auto_assign_enabled ? "on" : "off" },
        ]} />
      </Section>

      <Section kind="actions">
        <Kicker>i can</Kicker>
        <div className="ws-verbs">
          <button className="ws-verb" onClick={loadRoster}
                  data-testid={`provider-roster-toggle-${id}`}>≡ Roster</button>
          <button className="ws-verb" onClick={loadBilling}
                  data-testid={`provider-invoices-toggle-${id}`}>＄ Invoices</button>
          <button className="ws-verb" onClick={() => setContractOpen(v => !v)}
                  data-testid={`provider-contract-toggle-${id}`}>☐ Contract</button>
          <button className="ws-verb" onClick={doCall} disabled={!real?.contact_phone || busy}
                  data-testid={`provider-call-${id}`}>☎ Call dispatcher</button>
          <button className="ws-verb" onClick={() => setMsgOpen(v => !v)}
                  data-testid={`provider-message-toggle-${id}`}>✎ Message</button>
          <button className="ws-verb" onClick={openCoverage}
                  data-testid={`provider-coverage-${id}`}>↗ Open coverage</button>
          <button className="ws-verb" onClick={openTPPortal} disabled={portalLaunching}
                  data-testid={`provider-view-portal-${id}`}>
            {portalLaunching ? "opening…" : "⎘ View TP portal (admin)"}
          </button>
          <button className="ws-verb" onClick={issuePortalLink} disabled={issuingLink}
                  data-testid={`provider-issue-portal-${id}`}>
            {issuingLink ? "issuing…" : "⤴ Send portal link to TP"}
          </button>
        </div>
        {/* Portal link card — shown after VA issues access */}
        {portalLinkData && (
          <div style={{ marginTop: 10, padding: 12, background: "#F0FDF4", border: "1px solid #A7F3D0", borderRadius: 10, fontSize: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 6, color: "#166534" }}>Portal access issued</div>
            <div style={{ marginBottom: 4, color: "#334155" }}>
              <b>Email:</b> {portalLinkData.dispatcher_email} · <b>Temp pass:</b>{" "}
              <code style={{ background: "#E5F7EC", padding: "1px 5px", borderRadius: 3 }}>{portalLinkData.temp_password}</code>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
              <span style={{ color: "#334155", flex: 1, wordBreak: "break-all", fontSize: 11 }}>
                {portalLinkData.magic_url?.slice(0, 60)}…
              </span>
              <button className="ws-verb" style={{ shrink: 0 }}
                      onClick={() => { navigator.clipboard.writeText(portalLinkData.magic_url); toast.success("Magic link copied"); }}
                      data-testid={`provider-copy-magic-link-${id}`}>
                Copy link
              </button>
            </div>
            <div style={{ marginTop: 4, fontSize: 10, color: "#64748B" }}>Link valid 48 hrs · no password needed · sends TP straight to their portal</div>
          </div>
        )}
        {coverageNote && <div className="ws-rec-hint">{coverageNote}</div>}
      </Section>

      {rosterOpen && (
        <Section kind="evidence">
          <Kicker>roster</Kicker>
          {roster === null && <div className="ws-rec-hint">loading…</div>}
          {roster?.length === 0 && <div className="ws-rec-hint">no drivers on this TP's roster yet</div>}
          <ol className="ws-ledger">
            {(roster || []).map(dr => (
              <li key={dr.id}><span>{dr.name}</span><span>{dr.shift_status || "—"}</span></li>
            ))}
          </ol>
        </Section>
      )}

      {billingOpen && (
        <Section kind="evidence">
          <Kicker>billing</Kicker>
          {billing === null && <div className="ws-rec-hint">loading…</div>}
          {billing?.error && <div className="ws-rec-hint">couldn't load billing</div>}
          {billing && !billing.error && (
            <StatChips items={[
              { k: "dispatch mode", v: billing.dispatch_mode },
              { k: "billing status", v: billing.billing?.status || "none on file" },
            ]} />
          )}
        </Section>
      )}

      {contractOpen && real && (
        <Section kind="evidence">
          <Kicker>contract terms</Kicker>
          <StatChips items={[
            { k: "wait time", v: `${real.wait_time_minutes}m` },
            { k: "no-show wait", v: `${real.no_show_wait_minutes}m` },
            { k: "dead miles paid", v: real.pays_dead_miles ? `$${real.dead_mile_rate}/mi` : "no" },
            { k: "offer timeout", v: `${real.offer_timeout_seconds}s` },
            { k: "dispatch lead", v: real.dispatch_lead_time_minutes != null ? `${real.dispatch_lead_time_minutes}m` : "—" },
          ]} />
        </Section>
      )}

      {msgOpen && (
        <Section kind="preflight">
          <Kicker>message {name} dispatch</Kicker>
          <textarea className="ws-input" style={{ minHeight: 70 }} value={msgText}
                    onChange={e => setMsgText(e.target.value)} placeholder="what should they know?"
                    data-testid={`provider-message-text-${id}`} />
          <button className="ws-primary" style={{ marginTop: 8 }} disabled={msgSending || !msgText.trim()}
                  onClick={sendMsg} data-testid={`provider-message-send-${id}`}>
            {msgSending ? "sending…" : "Send"}
          </button>
        </Section>
      )}
    </>
  );
}

// ─── Compose new trip ──────────────────────────────────────────────
function ComposeTrip({ network, meta }) {
  const { createTrip, releaseFocus } = useRoom();
  const [rider, setRider] = useState("");
  const [phone, setPhone] = useState("");
  const [pickup, setPickup] = useState(meta?.prefillPickup || "");
  const [dropoff, setDropoff] = useState("");
  const [mob, setMob] = useState("ambulatory");
  const [pickupDate, setPickupDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [pickupTime, setPickupTime] = useState("");
  const [apptTime, setApptTime] = useState("");
  const [driverNotes, setDriverNotes] = useState("");
  const [showNotes, setShowNotes] = useState(false);
  // B-leg (return)
  const [addReturn, setAddReturn] = useState(false);
  const [returnDate, setReturnDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [returnTime, setReturnTime] = useState("");
  const [returnApptTime, setReturnApptTime] = useState("");
  const [returnBrokerTripId, setReturnBrokerTripId] = useState("");
  const [returnNotes, setReturnNotes] = useState("");
  // C-leg (pharmacy / extra stop)
  const [addPharmacy, setAddPharmacy] = useState(false);
  const [pharmAddress, setPharmAddress] = useState("");
  const [pharmDate, setPharmDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [pharmTime, setPharmTime] = useState("");
  const [pharmBrokerTripId, setPharmBrokerTripId] = useState("");
  const [pharmNotes, setPharmNotes] = useState("");

  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const toISO = (date, time) => {
    if (!date) return null;
    if (!time) return new Date(date).toISOString();
    return new Date(`${date}T${time}:00`).toISOString();
  };

  const canCreate = rider.trim() && pickup.trim() && dropoff.trim() && pickupDate && !creating;

  const submit = async () => {
    if (!canCreate) return;
    setCreating(true);
    setError(null);
    try {
      const requestedPickup = toISO(pickupDate, pickupTime) || new Date(Date.now() + 30 * 60000).toISOString();
      const payload = {
        rider: { name: rider.trim(), phone: phone.trim() ? autoPrefixUS(phone.trim()) : null, mobility_type: mob },
        pickup: { address: pickup.trim() },
        dropoff: { address: dropoff.trim() },
        requested_pickup: requestedPickup,
        appointment_time: apptTime ? toISO(pickupDate, apptTime) : null,
        notes: driverNotes.trim() || null,
        source: "manual",
        leg_label: (addReturn || addPharmacy) ? "A" : null,
      };
      const res = await createTrip(payload);
      if (!res.ok) { setError(res.error); return; }

      const parentId = res.trip?.trip_id;

      // B-leg (return)
      if (addReturn && returnTime && parentId) {
        await createTrip({
          rider: payload.rider,
          pickup: { address: dropoff.trim() },
          dropoff: { address: pickup.trim() },
          requested_pickup: toISO(returnDate || pickupDate, returnTime),
          appointment_time: returnApptTime ? toISO(returnDate || pickupDate, returnApptTime) : null,
          notes: returnNotes.trim() || driverNotes.trim() || null,
          broker_trip_id: returnBrokerTripId.trim() || null,
          source: "manual",
          parent_trip_id: parentId,
          leg_label: "B",
        });
      }

      // C-leg (pharmacy)
      if (addPharmacy && pharmAddress.trim() && parentId) {
        await createTrip({
          rider: payload.rider,
          pickup: { address: dropoff.trim() },
          dropoff: { address: pharmAddress.trim() },
          requested_pickup: pharmTime ? toISO(pharmDate || pickupDate, pharmTime) : toISO(pickupDate, pickupTime) || new Date(Date.now() + 30 * 60000).toISOString(),
          notes: pharmNotes.trim() || driverNotes.trim() || null,
          broker_trip_id: pharmBrokerTripId.trim() || null,
          source: "manual",
          parent_trip_id: parentId,
          leg_label: "C",
        });
      }

      releaseFocus();
    } catch (e) {
      setError(String(e));
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <Section kind="mission">
        <Kicker>compose · new trip</Kicker>
        <H1>{"who's riding"}</H1>
        <input className="ws-input" placeholder="rider name"
          value={rider} onChange={e => setRider(e.target.value)} autoFocus
          data-testid="compose-rider-input" />
        <input className="ws-input" style={{ marginTop: 8 }} placeholder="rider phone (optional)"
          value={phone} onChange={e => setPhone(e.target.value)}
          onBlur={() => setPhone((p) => (p.trim() ? autoPrefixUS(p) : p))}
          data-testid="compose-rider-phone-input" />
      </Section>

      <Section kind="ai">
        <Kicker>where &amp; when</Kicker>
        <input className="ws-input" placeholder="pickup address"
          value={pickup} onChange={e => setPickup(e.target.value)}
          data-testid="compose-pickup-input" />
        <input className="ws-input" placeholder="dropoff" style={{ marginTop: 8 }}
          value={dropoff} onChange={e => setDropoff(e.target.value)}
          data-testid="compose-dropoff-input" />
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <div style={{ flex: 1 }}>
            <div className="ws-field-label">Trip date</div>
            <input className="ws-input" type="date" value={pickupDate}
              onChange={e => setPickupDate(e.target.value)}
              data-testid="compose-pickup-date" />
          </div>
          <div style={{ flex: 1 }}>
            <div className="ws-field-label">Pickup time</div>
            <input className="ws-input" type="time" value={pickupTime}
              onChange={e => setPickupTime(e.target.value)}
              data-testid="compose-pickup-time" />
          </div>
        </div>
        <div style={{ marginTop: 8 }}>
          <div className="ws-field-label">Appointment time <span style={{ opacity: 0.5 }}>(optional)</span></div>
          <input className="ws-input" type="time" value={apptTime}
            onChange={e => setApptTime(e.target.value)}
            data-testid="compose-appt-time" />
        </div>
      </Section>

      <Section kind="actions">
        <Kicker>how</Kicker>
        <div className="ws-verbs">
          {["ambulatory", "wheelchair", "stretcher"].map(m => (
            <button key={m} className={`ws-verb ${mob === m ? "on" : ""}`} onClick={() => setMob(m)}
                    data-testid={`compose-mobility-${m}`}>{m}</button>
          ))}
        </div>

        {/* Driver notes toggle */}
        <button className="ws-verb" style={{ marginTop: 10, width: "100%", justifyContent: "center", opacity: 0.7 }}
                onClick={() => setShowNotes(v => !v)} data-testid="compose-toggle-notes">
          {showNotes ? "hide notes" : "+ driver notes"}
        </button>
        {showNotes && (
          <textarea className="ws-input" style={{ marginTop: 8, minHeight: 72, resize: "vertical" }}
            placeholder="notes for the driver (address code, unit #, instructions…)"
            value={driverNotes} onChange={e => setDriverNotes(e.target.value)}
            data-testid="compose-driver-notes" />
        )}

        {/* Return leg (B) */}
        <div style={{ marginTop: 12 }}>
          <button className={`ws-verb ${addReturn ? "on" : ""}`}
                  onClick={() => setAddReturn(v => !v)} data-testid="compose-toggle-return">
            + return leg (B)
          </button>
        </div>
        {addReturn && (
          <div style={{ marginTop: 8, padding: "10px 12px", border: "1px solid rgba(100,180,100,0.3)", borderRadius: 8, background: "rgba(0,0,0,0.03)" }}>
            <div className="ws-field-label" style={{ marginBottom: 6 }}>Return leg — {dropoff || "dropoff"} → {pickup || "pickup"}</div>
            <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
              <div style={{ flex: 1 }}>
                <div className="ws-field-label">Date</div>
                <input className="ws-input" type="date" value={returnDate}
                  onChange={e => setReturnDate(e.target.value)} data-testid="compose-return-date" />
              </div>
              <div style={{ flex: 1 }}>
                <div className="ws-field-label">Pickup time *</div>
                <input className="ws-input" type="time" value={returnTime}
                  onChange={e => setReturnTime(e.target.value)} data-testid="compose-return-time" />
              </div>
              <div style={{ flex: 1 }}>
                <div className="ws-field-label">Appt time</div>
                <input className="ws-input" type="time" value={returnApptTime}
                  onChange={e => setReturnApptTime(e.target.value)} data-testid="compose-return-appt-time" />
              </div>
            </div>
            <input className="ws-input" placeholder="B-leg broker / Kaizen trip ID (optional)"
              value={returnBrokerTripId} onChange={e => setReturnBrokerTripId(e.target.value)}
              data-testid="compose-return-broker-trip-id" />
            <input className="ws-input" placeholder="B-leg driver notes (optional)" style={{ marginTop: 6 }}
              value={returnNotes} onChange={e => setReturnNotes(e.target.value)}
              data-testid="compose-return-notes" />
          </div>
        )}

        {/* Pharmacy / C-leg */}
        <div style={{ marginTop: 8 }}>
          <button className={`ws-verb ${addPharmacy ? "on" : ""}`}
                  onClick={() => setAddPharmacy(v => !v)} data-testid="compose-toggle-pharmacy">
            + pharmacy / extra stop (C)
          </button>
        </div>
        {addPharmacy && (
          <div style={{ marginTop: 8, padding: "10px 12px", border: "1px solid rgba(100,100,200,0.3)", borderRadius: 8, background: "rgba(0,0,0,0.03)" }}>
            <div className="ws-field-label" style={{ marginBottom: 6 }}>Extra stop / pharmacy leg</div>
            <input className="ws-input" placeholder="stop address (pharmacy, lab, etc.)"
              value={pharmAddress} onChange={e => setPharmAddress(e.target.value)}
              data-testid="compose-pharm-address" />
            <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
              <div style={{ flex: 1 }}>
                <div className="ws-field-label">Date</div>
                <input className="ws-input" type="date" value={pharmDate}
                  onChange={e => setPharmDate(e.target.value)} data-testid="compose-pharm-date" />
              </div>
              <div style={{ flex: 1 }}>
                <div className="ws-field-label">Pickup time</div>
                <input className="ws-input" type="time" value={pharmTime}
                  onChange={e => setPharmTime(e.target.value)} data-testid="compose-pharm-time" />
              </div>
            </div>
            <input className="ws-input" placeholder="C-leg broker / Kaizen trip ID (optional)" style={{ marginTop: 6 }}
              value={pharmBrokerTripId} onChange={e => setPharmBrokerTripId(e.target.value)}
              data-testid="compose-pharm-broker-trip-id" />
            <input className="ws-input" placeholder="C-leg driver notes (optional)" style={{ marginTop: 6 }}
              value={pharmNotes} onChange={e => setPharmNotes(e.target.value)}
              data-testid="compose-pharm-notes" />
          </div>
        )}

        {error && <div className="ws-rec-hint" style={{ color: "#ef4444", marginTop: 8 }}>{error}</div>}
        <button className="ws-primary" style={{ marginTop: 16, width: "100%" }}
                disabled={!canCreate} onClick={submit} data-testid="compose-submit">
          {creating ? "creating…" : `Create trip${addReturn ? " + return" : ""}${addPharmacy ? " + stop" : ""}`}
        </button>
      </Section>
    </>
  );
}

// ─── Compose new CPD trip ─────────────────────────────────────────
// Chicago PD trips normally start from a live call or an officer's
// self-serve email form (cpd_program.py create_intake). This lets a VA
// key one in directly on The Floor — same pipeline, same immediate VA
// alert + escalation ladder, just triggered manually instead of by voice.
function ComposeCpdTrip() {
  const { releaseFocus } = useRoom();
  const [officerName, setOfficerName] = useState("");
  const [officerPhone, setOfficerPhone] = useState("");
  const [officerEmail, setOfficerEmail] = useState("");
  const [unitNumber, setUnitNumber] = useState("");
  const [caseNumber, setCaseNumber] = useState("");
  const [pickup, setPickup] = useState("");
  const [dropoff, setDropoff] = useState("");
  const [notes, setNotes] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);

  useEffect(() => {
    apiClient.get("/cpd/templates").then(r => setTemplates(r.data || [])).catch(() => {});
  }, []);

  const applyTemplate = (id) => {
    const t = templates.find(x => x.id === id);
    if (!t) return;
    setPickup(t.pickup_address);
    setDropoff(t.dropoff_address);
  };

  const saveTemplate = async () => {
    if (!templateName.trim() || !pickup.trim() || !dropoff.trim()) return;
    setSavingTemplate(true);
    try {
      const { data } = await apiClient.post("/cpd/templates", {
        label: templateName.trim(), pickup_address: pickup.trim(), dropoff_address: dropoff.trim(),
      });
      setTemplates(prev => [...prev, data]);
      setTemplateName("");
      toast.success("Route saved as a quick-pick");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save route"); }
    finally { setSavingTemplate(false); }
  };

  const emailValid = !officerEmail.trim() || /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(officerEmail.trim());
  const canCreate = officerPhone.trim() && emailValid && pickup.trim() && dropoff.trim() && !creating;
  const [emailRemembered, setEmailRemembered] = useState(false);

  const lookupOfficer = async (phone) => {
    if (!phone.trim()) return;
    try {
      const { data } = await apiClient.get("/cpd/officers/lookup", { params: { phone: autoPrefixUS(phone.trim()) } });
      if (data?.email && !officerEmail.trim()) {
        setOfficerEmail(data.email);
        if (data.name && !officerName.trim()) setOfficerName(data.name);
        setEmailRemembered(true);
        toast.success(`Remembered ${data.name || "this officer"}'s email`);
      }
    } catch { /* not found — fine, dispatch enters it manually */ }
  };

  const submit = async () => {
    if (!canCreate) return;
    setCreating(true);
    setError(null);
    try {
      await apiClient.post("/cpd/intakes/manual", {
        officer_name: officerName.trim() || null,
        officer_phone: autoPrefixUS(officerPhone.trim()),
        officer_email: officerEmail.trim() || null,
        unit_number: unitNumber.trim() || null,
        case_number: caseNumber.trim() || null,
        pickup_address: pickup.trim(),
        dropoff_address: dropoff.trim(),
        notes: notes.trim() || null,
      });
      toast.success("CPD trip created · alerting dispatch now");
      releaseFocus();
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't create this CPD trip");
    } finally { setCreating(false); }
  };

  return (
    <>
      <Section kind="mission">
        <Kicker>compose · new CPD trip</Kicker>
        <H1>officer & unit</H1>
        <input className="ws-input" placeholder="officer name (optional)"
          value={officerName} onChange={e => setOfficerName(e.target.value)} autoFocus
          data-testid="compose-cpd-officer-name" />
        <input className="ws-input" style={{ marginTop: 8 }} placeholder="officer phone (callback)"
          value={officerPhone} onChange={e => setOfficerPhone(e.target.value)}
          onBlur={(e) => { const v = e.target.value.trim() ? autoPrefixUS(e.target.value) : e.target.value; setOfficerPhone(v); lookupOfficer(v); }}
          data-testid="compose-cpd-officer-phone" />
        <input className="ws-input" style={{ marginTop: 8 }} placeholder="officer email (optional · sends the tracking link if provided)"
          value={officerEmail} onChange={e => { setOfficerEmail(e.target.value); setEmailRemembered(false); }}
          data-testid="compose-cpd-officer-email" />
        {emailRemembered && (
          <div className="ws-rec-hint" data-testid="compose-cpd-email-remembered-hint">✓ remembered from a previous trip — edit if this looks wrong</div>
        )}
        <div className="ws-verbs" style={{ marginTop: 8 }}>
          <input className="ws-input" placeholder="unit #" value={unitNumber}
            onChange={e => setUnitNumber(e.target.value)} data-testid="compose-cpd-unit" />
          <input className="ws-input" placeholder="case #" value={caseNumber}
            onChange={e => setCaseNumber(e.target.value)} data-testid="compose-cpd-case" />
        </div>
      </Section>
      <Section kind="ai">
        <Kicker>where</Kicker>
        {templates.length > 0 && (
          <select className="ws-input" style={{ marginBottom: 8 }} defaultValue="" onChange={(e) => e.target.value && applyTemplate(e.target.value)}
                  data-testid="compose-cpd-template-select">
            <option value="">Quick route — pick a saved pair…</option>
            {templates.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        )}
        <input className="ws-input" placeholder="pickup address"
          value={pickup} onChange={e => setPickup(e.target.value)}
          data-testid="compose-cpd-pickup" />
        <input className="ws-input" style={{ marginTop: 8 }} placeholder="dropoff address"
          value={dropoff} onChange={e => setDropoff(e.target.value)}
          data-testid="compose-cpd-dropoff" />
        <textarea className="ws-input" style={{ marginTop: 8, minHeight: 60 }} placeholder="notes for dispatch (optional)"
          value={notes} onChange={e => setNotes(e.target.value)} data-testid="compose-cpd-notes" />
        {pickup.trim() && dropoff.trim() && (
          <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
            <input className="ws-input" placeholder="save this route as…" value={templateName}
              onChange={e => setTemplateName(e.target.value)} data-testid="compose-cpd-template-name" />
            <button className="ws-verb" disabled={!templateName.trim() || savingTemplate} onClick={saveTemplate}
                    data-testid="compose-cpd-save-template">
              {savingTemplate ? "…" : "Save route"}
            </button>
          </div>
        )}
      </Section>
      <Section kind="actions">
        {!emailValid && officerEmail.trim() && (
          <div className="ws-rec-hint" style={{ color: "#ef4444" }}>enter a valid officer email</div>
        )}
        {error && <div className="ws-rec-hint" style={{ color: "#ef4444" }}>{error}</div>}
        <button className="ws-primary" style={{ marginTop: 8, width: "100%" }}
                disabled={!canCreate} onClick={submit} data-testid="compose-cpd-submit">
          {creating ? "creating…" : "Create CPD trip · alert dispatch"}
        </button>
      </Section>
    </>
  );
}

// ─── Broadcast composer — real, immediate SMS fan-out to on-shift drivers
// (operational, not the marketing-approval pipeline under Admin → Marketing).
function BroadcastView({ network }) {
  const { sendBroadcast } = useRoom();
  const [scope, setScope] = useState("chicago");
  const [onlineOnly, setOnlineOnly] = useState(true);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const scopes = [
    { key: "chicago", label: "◐ Chicago drivers" },
    { key: "houston", label: "◐ Houston drivers" },
    { key: "all",     label: "◐ Entire network" },
  ];

  const send = async () => {
    if (!message.trim()) return;
    setSending(true);
    setResult(null);
    const res = await sendBroadcast(scope, message.trim(), onlineOnly);
    setSending(false);
    setResult(res);
    if (res?.ok && res.sent > 0) setMessage("");
  };

  return (
    <>
      <Section kind="mission">
        <Kicker>broadcast</Kicker>
        <H1>to whom</H1>
      </Section>
      <Section kind="ai">
        <div className="ws-verbs">
          {scopes.map(s => (
            <button key={s.key} className={`ws-verb ${scope === s.key ? "on" : ""}`}
                    onClick={() => setScope(s.key)} data-testid={`broadcast-scope-${s.key}`}>
              {s.label}
            </button>
          ))}
        </div>
        <label className="ws-rec-hint" style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
          <input type="checkbox" checked={onlineOnly} onChange={e => setOnlineOnly(e.target.checked)}
                 data-testid="broadcast-online-only" />
          on-shift drivers only
        </label>
        <textarea className="ws-input" style={{ minHeight: 100, marginTop: 12 }}
          placeholder="what should i tell them?" value={message}
          onChange={e => setMessage(e.target.value)} data-testid="broadcast-message" />
      </Section>
      <Section kind="actions">
        <button className="ws-primary" style={{ width: "100%" }} disabled={sending || !message.trim()}
                onClick={send} data-testid="broadcast-send">
          {sending ? "sending…" : "Send"}
        </button>
        {result && (
          <div className="ws-rec-hint" style={{ marginTop: 8 }} data-testid="broadcast-result">
            {result.error ? `failed · ${result.error}`
              : result.sent > 0 ? `sent to ${result.sent}/${result.total} driver(s)`
              : result.note || "no drivers matched this scope"}
          </div>
        )}
      </Section>
    </>
  );
}

// ─── Primitives ────────────────────────────────────────────────────
// ---------------------------------------------------------------------------
// TripNotesPanel — VA-editable notes for a trip on the Floor.
//
// Two distinct layers:
//  1. STRUCTURED FIELDS (rider.special_instructions / medical_notes / pickup.notes)
//     These come from the Kaizen offer's "Driver Notes" section and are shown
//     read-only if Kaizen filled them, or editable by the VA at any time.
//     Saved via PATCH /api/dispatch/trips/{booking_id}/rider-notes.
//
//  2. VA NOTE THREAD (trip_notes collection)
//     Timestamped, author-attributed notes VA adds while working the trip.
//     Saved via POST /api/dispatch/trips/{booking_id}/notes.
//     Displayed in reverse-chrono order.
// ---------------------------------------------------------------------------
function TripNotesPanel({ trip }) {
  const bookingId = trip?.legacy_booking_id || trip?.booking_id;

  // Structured fields state
  const [specialInstr, setSpecialInstr] = useState(trip?.rider?.special_instructions || "");
  const [medicalNotes, setMedicalNotes] = useState(trip?.rider?.medical_notes || "");
  const [pickupNotes, setPickupNotes] = useState(trip?.pickup?.notes || "");
  const [editingStructured, setEditingStructured] = useState(false);
  const [savingStructured, setSavingStructured] = useState(false);

  // VA thread state
  const [vaThread, setVaThread] = useState([]);
  const [threadLoaded, setThreadLoaded] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [threadExpanded, setThreadExpanded] = useState(false);

  // Sync fields when trip changes
  useEffect(() => {
    setSpecialInstr(trip?.rider?.special_instructions || "");
    setMedicalNotes(trip?.rider?.medical_notes || "");
    setPickupNotes(trip?.pickup?.notes || "");
  }, [trip?.legacy_booking_id]);

  const loadThread = async () => {
    if (!bookingId) return;
    try {
      const { data } = await apiClient.get(`/dispatch/trips/${bookingId}/notes`);
      setVaThread(Array.isArray(data) ? data : (data.notes || []));
    } catch (_) {}
    setThreadLoaded(true);
  };

  useEffect(() => {
    if (threadExpanded && !threadLoaded) loadThread();
  }, [threadExpanded]);

  const saveStructured = async () => {
    if (!bookingId) { toast.error("No booking ID — can't save notes"); return; }
    setSavingStructured(true);
    try {
      await apiClient.patch(`/dispatch/trips/${bookingId}/rider-notes`, {
        special_instructions: specialInstr.trim() || null,
        medical_notes: medicalNotes.trim() || null,
        pickup_notes: pickupNotes.trim() || null,
      });
      toast.success("Notes saved");
      setEditingStructured(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save notes");
    } finally {
      setSavingStructured(false);
    }
  };

  const addVaNote = async () => {
    if (!newNote.trim()) return;
    if (!bookingId) { toast.error("No booking ID"); return; }
    setAddingNote(true);
    try {
      await apiClient.post(`/dispatch/trips/${bookingId}/notes`, { body: newNote.trim() });
      setNewNote("");
      await loadThread();
      toast.success("Note added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add note");
    } finally {
      setAddingNote(false);
    }
  };

  const deleteVaNote = async (noteId) => {
    if (!bookingId) return;
    try {
      await apiClient.delete(`/dispatch/trips/${bookingId}/notes/${noteId}`);
      setVaThread(prev => prev.filter(n => n.id !== noteId));
    } catch (_) {
      toast.error("Couldn't delete note");
    }
  };

  const hasStructured = specialInstr || medicalNotes || pickupNotes;

  return (
    <Section kind="notes">
      <Kicker>trip notes</Kicker>

      {/* ── Structured (Kaizen + VA editable) ── */}
      <div className="ws-notes-structured" data-testid="trip-notes-structured">
        {!editingStructured ? (
          <>
            {hasStructured ? (
              <div className="ws-notes-readonly">
                {specialInstr && (
                  <div className="ws-note-field" data-testid="trip-note-special-instructions">
                    <span className="ws-note-label">driver notes</span>
                    <span className="ws-note-value">{specialInstr}</span>
                  </div>
                )}
                {medicalNotes && (
                  <div className="ws-note-field" data-testid="trip-note-medical">
                    <span className="ws-note-label">medical</span>
                    <span className="ws-note-value">{medicalNotes}</span>
                  </div>
                )}
                {pickupNotes && (
                  <div className="ws-note-field" data-testid="trip-note-pickup">
                    <span className="ws-note-label">access</span>
                    <span className="ws-note-value">{pickupNotes}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="ws-notes-empty-hint">no broker notes — add them below</div>
            )}
            <button
              className="ws-notes-edit-btn"
              onClick={() => setEditingStructured(true)}
              data-testid="trip-notes-edit-btn"
            >
              {hasStructured ? "edit notes" : "+ add broker notes"}
            </button>
          </>
        ) : (
          <div className="ws-notes-edit-form">
            <label className="ws-note-edit-label">Driver instructions</label>
            <textarea
              className="ws-note-textarea"
              value={specialInstr}
              onChange={e => setSpecialInstr(e.target.value)}
              placeholder="From Kaizen portal Driver Notes / broker instructions…"
              rows={2}
              data-testid="trip-note-edit-special-instructions"
            />
            <label className="ws-note-edit-label">Medical / mobility notes</label>
            <textarea
              className="ws-note-textarea"
              value={medicalNotes}
              onChange={e => setMedicalNotes(e.target.value)}
              placeholder="e.g. oxygen tank, power wheelchair, transfer assist…"
              rows={2}
              data-testid="trip-note-edit-medical"
            />
            <label className="ws-note-edit-label">Access / location notes</label>
            <textarea
              className="ws-note-textarea"
              value={pickupNotes}
              onChange={e => setPickupNotes(e.target.value)}
              placeholder="e.g. gate code 4812, park in rear, ring bell unit 3B…"
              rows={2}
              data-testid="trip-note-edit-pickup"
            />
            <div className="ws-notes-edit-actions">
              <button
                className="ws-verb"
                onClick={() => setEditingStructured(false)}
                disabled={savingStructured}
                data-testid="trip-notes-cancel-btn"
              >cancel</button>
              <button
                className="ws-primary ws-notes-save-btn"
                onClick={saveStructured}
                disabled={savingStructured}
                data-testid="trip-notes-save-btn"
              >{savingStructured ? "saving…" : "save notes"}</button>
            </div>
          </div>
        )}
      </div>

      {/* ── VA note thread ── */}
      <div className="ws-notes-thread" data-testid="trip-notes-thread">
        <button
          className="ws-notes-thread-toggle"
          onClick={() => setThreadExpanded(e => !e)}
          data-testid="trip-notes-thread-toggle"
        >
          {threadExpanded ? "▾ va notes" : "▸ va notes"}
          {vaThread.length > 0 && <span className="ws-notes-thread-count">{vaThread.length}</span>}
        </button>
        {threadExpanded && (
          <>
            <div className="ws-notes-add">
              <textarea
                className="ws-note-textarea ws-note-textarea--compact"
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
                placeholder="Add a VA note (visible to ops only)…"
                rows={2}
                data-testid="trip-va-note-input"
                onKeyDown={e => { if (e.key === "Enter" && e.metaKey) addVaNote(); }}
              />
              <button
                className="ws-primary ws-notes-save-btn"
                onClick={addVaNote}
                disabled={addingNote || !newNote.trim()}
                data-testid="trip-va-note-submit"
              >{addingNote ? "adding…" : "add note"}</button>
            </div>
            <div className="ws-notes-list">
              {vaThread.length === 0 && threadLoaded && (
                <div className="ws-notes-empty-hint">no VA notes yet</div>
              )}
              {vaThread.map(n => (
                <div key={n.id} className="ws-note-entry" data-testid={`va-note-${n.id}`}>
                  <span className="ws-note-author">{n.author || "VA"}</span>
                  <span className="ws-note-time">{new Date(n.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                  <span className="ws-note-body">{n.body}</span>
                  <button
                    className="ws-note-delete"
                    onClick={() => deleteVaNote(n.id)}
                    data-testid={`va-note-delete-${n.id}`}
                    title="delete this note"
                  >×</button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Section>
  );
}

function Section({ kind, children }) {
  return <div className={`ws-section ws-${kind}`}>{children}</div>;
}
function Kicker({ children }) { return <div className="ws-kicker">{children}</div>; }
function H1({ children }) { return <div className="ws-h1">{children}</div>; }
function Empty({ children }) { return <div className="ws-empty">{children}</div>; }
function StatChips({ items }) {
  return (
    <div className="ws-chips">
      {items.map((it, i) => (
        <span key={i} className={`ws-chip ${it.tone ? "tone-" + it.tone : ""}`}>
          <span className="ws-chip-k">{it.k}</span>
          <span className="ws-chip-v">{it.v}</span>
        </span>
      ))}
    </div>
  );
}
